import { AxiosError } from "axios";
import { useContext, useMemo, useRef } from "react";
import useSWR from "swr";
import { GetOrders200Response } from "../../codegen-api";
import { AuthContext } from "../AuthContext";
import {
  orderListSignature,
  useTradeSyncTarget,
} from "../../services/tradeSync";
import { WebsocketContext } from "../WebsocketAuthContext";
import { APIEndpointEnum } from "../../enums/endpoint";
import { authApi } from "../../services/api/apiFetcher";
import { pollingInterval } from "../../services/api/pollingInterval";
import { useSFX } from "../../hooks/useSFX";
import { useInstrumentResolver } from "../../hooks/markets/useInstrumentResolver";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useClock } from "../../hooks/api/clock/useClock";
import { isStaleApiCredentialError } from "../../utils/authError";
import { useAccountKeepPreviousData } from "../../hooks/api/useAccountKeepPreviousData";
import { mergeRecentRealtimeOrders } from "./helpers";
import { useRealtimeOrders } from "./useRealtimeOrders";
import { useOrderActions } from "./useOrderActions";
import { useTPSLActions } from "./useTPSLActions";

// Single source of truth for the `/orders` endpoint.
//
// Owns the ONE orders SWR subscription, the websocket-driven realtime
// reconciliation, polling and all order actions (create/edit/remove/TP-SL/
// close). Every consumer reads the shared value via `useOrder()` (a thin
// `useContext` reader) instead of each spinning up its own `/orders` fetch
// driver + realtime effect.
function useOrderController() {
  // Get API Key
  const { apiKey, apiSecret, signingKey, account, deleteAuthInfo } =
    useContext(AuthContext);
  const {
    authenticated,
    triggerSubscribe,
    triggerUnsubscribe,
    registerRevalidate,
    lastMessages,
  } = useContext(WebsocketContext);
  const { playSound } = useSFX();
  const { mutate: mutateAccount } = useGetAccount();
  const { getTimestamp } = useClock();
  const { resolveMarket } = useInstrumentResolver();
  const accountKey = account?.toLowerCase();
  const revalidationTimersRef = useRef<number[]>([]);
  const lastRevalidationRef = useRef(0);
  const { keepPreviousData, trackData } = useAccountKeepPreviousData(accountKey);

  const fetcher = useMemo(
    () => authApi(apiKey, apiSecret),
    [apiKey, apiSecret]
  );

  const swr = useSWR<GetOrders200Response[], AxiosError>(
    apiKey && apiSecret && accountKey
      ? [APIEndpointEnum.ORDERS, accountKey, apiKey, apiSecret]
      : undefined,
    async () => {
      const orders = (await (await fetcher.getOrders())()).data;
      return mergeRecentRealtimeOrders(accountKey, orders);
    },
    {
      dedupingInterval: 10000,
      refreshInterval: pollingInterval[APIEndpointEnum.ORDERS],
      // Keep previous data ONLY while a live account is selected, so a disconnect
      // (accountKey -> undefined) drops the stale orders instead of retaining
      // them under a "Connect Wallet" header. SWR holds keepPreviousData in a
      // per-hook laggy ref that a cache clear can't reach — gating it here is the
      // only reliable way to clear it on disconnect. A wallet switch also drops
      // it until the new account's data arrives (FE-14).
      keepPreviousData,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      onError: (error) => {
        if (isStaleApiCredentialError(error)) {
          deleteAuthInfo("api");
          mutateOrders(undefined, { revalidate: false });
        }
      },
    }
  );
  const { mutate: mutateOrders } = swr;
  trackData(swr.data);

  // Converge the legacy `/orders` cache after a confirmed trade. Action sites
  // call `revalidateOrdersSoon` directly; this covers perp trades, which
  // previously relied on the broad sweep.
  useTradeSyncTarget({
    targetId: `orders:${accountKey || "anon"}`,
    enabled: Boolean(accountKey),
    current: () => swr.data,
    refetch: () => swr.mutate(),
    signature: orderListSignature,
  });

  const revalidateOrdersSoon = useRealtimeOrders({
    account,
    accountKey,
    authenticated,
    triggerSubscribe,
    triggerUnsubscribe,
    registerRevalidate,
    lastMessages,
    resolveMarket,
    mutateOrders,
    orders: swr.data,
    revalidationTimersRef,
    lastRevalidationRef,
  });

  const { createOrder, removeOrder, removeAllOrders, editOrder } =
    useOrderActions({
      signingKey,
      account,
      getTimestamp,
      fetcher,
      playSound,
      revalidateOrdersSoon,
      deleteAuthInfo,
      swr,
    });

  const {
    closePosition,
    createTPSLOrders,
    tpslSupported,
    tpslTriggersForPosition,
  } = useTPSLActions({
    createOrder,
    mutateAccount,
  });

  return useMemo(
    () => ({
      ...swr,
      revalidateOrdersSoon,
      createOrder,
      removeOrder,
      removeAllOrders,
      editOrder,
      createTPSLOrders,
      tpslSupported,
      tpslTriggersForPosition,
      closePosition,
    }),
    [
      swr,
      revalidateOrdersSoon,
      createOrder,
      removeOrder,
      removeAllOrders,
      editOrder,
      createTPSLOrders,
      tpslSupported,
      tpslTriggersForPosition,
      closePosition,
    ]
  );
}

export type IOrderContextType = ReturnType<typeof useOrderController>;

export { useOrderController };
